import { apiRequest } from '../../api/client';
import { uploadBinary, fetchBlob } from '../../api/binary';

// Mirrors api/src/gym/member-import-routes.js's own MAX_UPLOAD_BYTES (5 MB):
// checking it here lets the upload step reject an oversized file before ever
// opening a connection, instead of waiting on a round trip just to hear back
// `413 file_too_large`.
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

const SUMMARY_HEADER = 'X-Import-Summary';

const EXTENSION_TYPES = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
};

// The server's own filter (`acceptSpreadsheet` in member-import-routes.js)
// requires a Content-Type header that matches one of a short allow-list, and
// `application/octet-stream` is always in it as a catch-all — so when the
// browser hands back an empty `file.type` (common for `.csv` on some OS/
// browser combinations), guessing by extension (or falling back to that
// catch-all) is always something the server will accept, never a rejection
// this client invents on its own.
export function contentTypeFor(file) {
  if (file && file.type) return file.type;
  const name = (file && file.name) || '';
  const extension = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  return EXTENSION_TYPES[extension] || 'application/octet-stream';
}

/**
 * `/members/import/*` — HAMMER 1D's member-migration wizard (task E6),
 * consuming the E3 contract in `api/src/gym/member-import-routes.js` +
 * `member-import-service.js`.
 *
 * `upload` sends the file's own bytes as the raw request body (never JSON) —
 * the one write in this whole page that isn't `apiRequest` — because the
 * server reads `.xlsx`/`.csv` content directly (`express.raw`), not a
 * multipart form.
 *
 * `commit` and `downloadResult` answer a `.xlsx` blob (the created accounts'
 * temporary passwords), not JSON — see `fetchBlob` in `../../api/binary.js`.
 * `X-Import-Summary` carries the counts as a header so the page can show them
 * without ever parsing the workbook itself. `downloadResult` (per the E3 fix
 * round in progress alongside this task) re-fetches that same file during the
 * upload's TTL — used when a commit's response is lost to a network error, or
 * a retried commit comes back `409 import_already_committed` with
 * `resultAvailable: true`, so the temporary passwords aren't stranded on the
 * server after a connection drop. The client reads that flag from the nested
 * error envelope and downloads the result through E3's owner-checked GET route.
 */
export const importApi = {
  upload: (file, { fetchImpl } = {}) => uploadBinary('/members/import/uploads', file, {
    contentType: contentTypeFor(file),
    fetchImpl,
  }),
  preview: (input, { fetchImpl } = {}) => apiRequest('/members/import/preview', { method: 'POST', body: input, fetchImpl }),
  commit: (input, { fetchImpl } = {}) => fetchBlob('/members/import/commit', {
    method: 'POST',
    body: input,
    summaryHeader: SUMMARY_HEADER,
    fetchImpl,
  }),
  downloadResult: (uploadId, { fetchImpl } = {}) => fetchBlob(`/members/import/${encodeURIComponent(uploadId)}/result`, {
    method: 'GET',
    summaryHeader: SUMMARY_HEADER,
    fetchImpl,
  }),
};
